import React, { useState, useEffect, useCallback, useMemo } from 'react'
import instrument from '@adc/instrument'
import { get } from 'lodash'
import {
  withApplicationNotificationsContext,
  NOTIFICATION_ROLES,
} from '@adc/ui-adc'
import type { FeatureFlags } from '@adc/ui-adc'
import {
  type AuctionBlueprintOnlineReactiveArgs,
  type AuctionBlueprintScheduledOnlineReactiveArgs,
  type primitives,
} from '@adc/blueprint'
import { useListingContext } from 'common/context/listing'
import * as graph from '@adc/sdk'
import type { OnlineAuctionSegmentBidType } from '@adc/sdk/types'
import { internationalization } from '@adc/constants'
import * as errorMessages from 'common/constants/errorMessages'
import analytics from 'common/services/analytics'
import { formatMoneyWithCentsExceptZero } from 'common/utils/formatters'
import interpolate from 'common/utils/interpolate'
import { ZERO_DOLLARS } from 'common/components/bidBanner/constants'
import { ApiMapperBiddingError } from '../../../errors'
import { numberNoDecimals, numberWithDecimals } from '../../../utils/index'

type AuctionUser = primitives.AuctionUser

const CONFIRM_EMAIL_CONTENT = (
  <div>
    <p>
      Check your email to confirm your account. Then, log out of your account
      and log back in so we can confirm your status.
    </p>
    <p>This must be done in order to place bids.</p>
  </div>
)

const REFRESH_TOKEN_CONTENT =
  'Your bid cannot be placed at this time. Please log out of your account and log back in so we can confirm your status.'

const WARNING_DELAY_IN_MS = 500

const getErrorMessage = (errorCode: string) =>
  get(errorMessages, errorCode, errorMessages.defaultMessage)

export interface BidFormProps {
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  user: AuctionUser
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notifications?: any
  shouldShowOneAwayMessage?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any
  featureFlags: FeatureFlags
  onInstantlyOutbid?: () => void
}

const BidForm = ({
  user,
  notifications,
  shouldShowOneAwayMessage,
  children,
  featureFlags,
  onInstantlyOutbid,
}: BidFormProps) => {
  const { listing, segment } = useListingContext()

  const isUserConfirmed = user.logged_in
    ? user.status === graph.enums.UserStatus.Confirmed
    : false

  const isMaxBiddingActive = useMemo(
    () => segment?.__methods__.isMaxBiddingActive(),
    [segment]
  )

  const isAuctionActive = useMemo(
    () => segment?.__methods__.isOnlineAuctionActive(),
    [segment]
  )

  const getAppropriateDefaultBiddingMode = useCallback(() => {
    // if auction is in progress, default to regular bidding mode
    if (isAuctionActive) {
      return graph.enums.OnlineAuctionSegmentBidType.Regular
    } else {
      return graph.enums.OnlineAuctionSegmentBidType.MaxBid
    }
  }, [isAuctionActive])

  const [biddingMode, setBiddingMode] = useState<OnlineAuctionSegmentBidType>(
    getAppropriateDefaultBiddingMode()
  )

  const hasValidMaxBid = useMemo(
    () => segment?.__methods__.hasValidMaxBid(),
    [segment]
  )

  const getNextBidAmount = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const nextBidAmount =
      isMaxBiddingActive &&
      biddingMode === graph.enums.OnlineAuctionSegmentBidType.MaxBid
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          segment?.__methods__.getNextMaxBidAmount()
        : segment?.__methods__.getNextBidAmount()
    if (nextBidAmount !== null && nextBidAmount !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return nextBidAmount
    } else {
      console.warn('Next bid amount is not defined')
      return 0
    }
  }, [biddingMode, isMaxBiddingActive, segment?.__methods__])

  const [bidAmount, setBidAmount] = useState(getNextBidAmount())
  const [error, setError] = useState(false)
  const [touched, setTouched] = useState(false)
  const [isAdjustMode, setIsAdjustMode] = useState(false)
  const [isBidConfirmationOpen, setIsBidConfirmationOpen] = useState(false)
  const [isPerformingBid, setIsPerformingBid] = useState(false)
  const [shouldShowWarning, setShouldShowWarning] = useState(false)

  const handleBidButtonClick = (openConfirmationModalCallback?: () => void) => {
    if (user.logged_in && user.status === graph.enums.UserStatus.Confirmed) {
      setIsBidConfirmationOpen(true)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      openConfirmationModalCallback?.()
    } else if (user.logged_in) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      notifications.overlayBanner.open({
        message: CONFIRM_EMAIL_CONTENT,
        title: 'Confirm Your Email',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        role: NOTIFICATION_ROLES.ERROR,
        centered: false,
      })
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      notifications.overlayBanner.open({
        message: REFRESH_TOKEN_CONTENT,
        title: 'Refresh Token',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        role: NOTIFICATION_ROLES.ERROR,
        centered: false,
      })
    }
  }

  const hideBidConfirmation = () => {
    setIsBidConfirmationOpen(false)
  }

  const showBidAttemptToast = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (message: any, type: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (type === NOTIFICATION_ROLES.SUCCESS) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        notifications.overlayBanner.open({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          message,
          title: 'Thank You!',
        })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      } else if (type === NOTIFICATION_ROLES.ERROR) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        notifications.overlayBanner.open({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          message,
          title: 'Bid Error',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          role: NOTIFICATION_ROLES.ERROR,
        })
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    [notifications.overlayBanner]
  )

  const handleSuccess = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (res: any) => {
      hideBidConfirmation()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const displayBidAmount = formatMoneyWithCentsExceptZero(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        res.current_bid_amount || bidAmount,
        ZERO_DOLLARS
      )

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (res.was_instantly_outbid) {
        onInstantlyOutbid?.()
      }

      const message = interpolate(
        internationalization.transient_message_bid_was_placed,
        [displayBidAmount]
      )
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      showBidAttemptToast(message, NOTIFICATION_ROLES.SUCCESS)
      analytics.bidBanner.confirmBid.success(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (res.current_bid_amount as number) || bidAmount
      )

      instrument.increment('bidding_analytics', 1, [
        `status:success`,
        `version:v2`,
        `action:bid`,
        `scope:handleSuccess`,
      ])
    },
    [bidAmount, showBidAttemptToast, onInstantlyOutbid]
  )

  const handleMaxBidSuccess = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (maxBidAmount: number, res?: any) => {
      hideBidConfirmation()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const displayBidAmount = formatMoneyWithCentsExceptZero(
        maxBidAmount,
        ZERO_DOLLARS
      )

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (res?.was_instantly_outbid) {
        onInstantlyOutbid?.()
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const message = interpolate(
        internationalization.transient_message_max_bid_was_placed,
        [displayBidAmount]
      )
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      showBidAttemptToast(message, NOTIFICATION_ROLES.SUCCESS)
      if (isAdjustMode) {
        analytics.maxBid.trackAdjustAutoBidConfirm.success(
          listing,
          maxBidAmount
        )
      } else if (hasValidMaxBid) {
        analytics.maxBid.trackIncreaseAutoBidConfirm.success(
          listing,
          maxBidAmount
        )
      } else {
        analytics.maxBid.trackSetAutoBidConfirm.success(listing, maxBidAmount)
      }
      instrument.increment('bidding_analytics', 1, [
        `status:success`,
        `version:v2`,
        `action:max_bid`,
        `scope:handleMaxBidSuccess`,
      ])
    },
    [
      showBidAttemptToast,
      hasValidMaxBid,
      listing,
      onInstantlyOutbid,
      isAdjustMode,
    ]
  )

  const cleanUp = useCallback(() => {
    setIsPerformingBid(false)
    setTouched(false)
    setBiddingMode(getAppropriateDefaultBiddingMode())
    setIsAdjustMode(false)
  }, [getAppropriateDefaultBiddingMode])

  const updateBidAmountProgrammatically = useCallback(() => {
    if (biddingMode === graph.enums.OnlineAuctionSegmentBidType.Regular) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const bidAmount = getNextBidAmount()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setBidAmount(bidAmount)
    }
    setError(false)
    setShouldShowWarning(false)
    setTouched(false)
  }, [biddingMode, getNextBidAmount])

  const performBidV2 = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleError = (e: any, action = '', isMaxBid: boolean) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const err = new ApiMapperBiddingError(e)
      hideBidConfirmation()
      if (isMaxBid) {
        if (isAdjustMode) {
          analytics?.maxBid?.trackAdjustAutoBidConfirm?.fail?.(listing)
        } else if (hasValidMaxBid) {
          analytics?.maxBid?.trackIncreaseAutoBidConfirm?.fail?.(listing)
        } else {
          analytics?.maxBid?.trackSetAutoBidConfirm?.fail?.(listing)
        }
      } else {
        analytics?.bidBanner?.confirmBid?.fail?.()
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const errorCode = get(err, 'code')

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const errorMessage = getErrorMessage(errorCode)

      instrument.increment(`bidding_analytics`, 1, [
        `status:failure`,
        `version:v2`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `error: ${errorCode || ''}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `http_code: ${get(err, 'errorBody.status')}`,
        `action:${action}`,
        `scope:handleError`,
      ])

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      showBidAttemptToast(errorMessage, NOTIFICATION_ROLES.ERROR)

      // If the bid attempt failed for a reason other than short-term session expiration,
      // the most likely cause is that another user placed a bid
      // while the first user's BidConfirmationModal was open.
      updateBidAmountProgrammatically()
    }

    setIsPerformingBid(true)

    if (
      isMaxBiddingActive &&
      biddingMode === graph.enums.OnlineAuctionSegmentBidType.MaxBid
    ) {
      segment?.__methods__
        .upsertListingOnlineMaxBid({
          max_bid_amount: bidAmount,
          bidder_party_id: (user.logged_in && user.party_id) || '',
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => handleMaxBidSuccess(bidAmount, res))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((e: any) => handleError(e, 'max_bid', true))
        .finally(cleanUp)
    } else {
      if (shouldShowOneAwayMessage) {
        instrument.increment('bidding.action', 1, [
          `action:regular_bid`,
          `phase:one_away`,
        ])
      }

      segment?.__methods__
        .submitOnlineRegularBid({
          bid_amount: bidAmount,
          // @ts-ignore type says mismatch but its correct
          type: graph.enums.OnlineAuctionSegmentBidType.Regular,
          bidder_party_id: (user.logged_in && user.party_id) || '',
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => handleSuccess(res))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((e: any) => handleError(e, 'bid', false))
        .finally(cleanUp)
    }
  }, [
    bidAmount,
    biddingMode,
    cleanUp,
    hasValidMaxBid,
    handleMaxBidSuccess,
    handleSuccess,
    isAdjustMode,
    isMaxBiddingActive,
    listing,
    segment?.__methods__,
    shouldShowOneAwayMessage,
    showBidAttemptToast,
    updateBidAmountProgrammatically,
    user.logged_in,
    // @ts-ignore party_id exists here, type doesn't match exactly
    user.party_id,
  ])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const inputValue = e.target.value || String(0)
    const bidAmount = parseFloat(
      segment.configuration.precision_unit ===
        graph.enums.MoneyDenomination.Cent
        ? numberWithDecimals(inputValue)
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          numberNoDecimals(inputValue)
    )
    setTouched(true)
    if (bidAmount >= getNextBidAmount()) {
      setBidAmount(bidAmount)
      setError(false)
      setShouldShowWarning(false)
    } else {
      setBidAmount(bidAmount)
      setError(true)
      setTimeout(() => setShouldShowWarning(error), WARNING_DELAY_IN_MS)
    }
  }

  useEffect(() => {
    if (!touched) {
      updateBidAmountProgrammatically()
    }
  }, [
    touched,
    segment?.current_user_max_bid?.max_bid_amount,
    segment?.current_user_highest_bid,
    updateBidAmountProgrammatically,
  ])

  const warning = error && shouldShowWarning

  const childrenProps = {
    bidAmount,
    handleBidButtonClick,
    hasError: error,
    hideBidConfirmation,
    isBidConfirmationOpen,
    biddingMode,
    setBiddingMode,
    isPerformingBid,
    isMaxBiddingActive,
    isUserConfirmed,
    onChange,
    performBid: performBidV2,
    warning,
    listing,
    segment,
    featureFlags,
    user,
    cleanUp,
    setIsAdjustMode,
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
  return children(childrenProps)
}

export { BidForm }
// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default withApplicationNotificationsContext(BidForm)
